// src/pages/EventDetails.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Chip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PeopleIcon from '@mui/icons-material/People';
import RepeatIcon from '@mui/icons-material/Repeat';
import AddAlarmIcon from '@mui/icons-material/AddAlarm';
import { getEventById } from '../services/eventService';
import { Event } from '../types/church';
import CreateAlarmDialog from '../components/CreateAlarmDialog';

const EventDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openAlarmDialog, setOpenAlarmDialog] = useState(false);

  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const eventData = await getEventById(Number(id));
        setEvent(eventData);
      } catch (err: any) {
        console.error('Failed to fetch event details:', err);
        setError(err.response?.data?.detail || 'Failed to fetch event details.');
      } finally {
        setLoading(false);
      }
    };
    fetchEventDetails();
  }, [id]);

  const handleCreateAlarmSuccess = () => {
    setOpenAlarmDialog(false);
    // Optionally refresh event to get updated alarm count
    if (id) {
      getEventById(Number(id)).then(setEvent);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !event) {
    return <Alert severity="error">{error || 'Événement non trouvé'}</Alert>;
  }

  const isPastEvent = new Date(event.date) < new Date();

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/churches/${event.eglise_id}`)}
        sx={{ mb: 2 }}
      >
        Retour à l'église
      </Button>

      <Paper sx={{ mb: 3, overflow: 'hidden', borderRadius: 3 }}>
        {/* Event Image Banner */}
        {event.image_url && (
          <Box
            sx={{
              width: '100%',
              height: 250,
              backgroundImage: `url(${event.image_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        )}

        <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {event.titre}
            </Typography>
            <Stack direction="row" spacing={1} mb={2}>
              {event.is_recurring && (
                <Chip
                  icon={<RepeatIcon />}
                  label={event.recurrence_pattern}
                  color="secondary"
                />
              )}
              {isPastEvent ? (
                <Chip label="Passé" color="default" />
              ) : (
                <Chip label="À venir" color="success" />
              )}
            </Stack>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddAlarmIcon />}
            onClick={() => setOpenAlarmDialog(true)}
            disabled={isPastEvent}
          >
            Créer une alarme
          </Button>
        </Stack>

        <Typography variant="body1" sx={{ mb: 3 }}>
          {event.description}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Informations
                </Typography>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarTodayIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Date et heure
                      </Typography>
                      <Typography variant="body2">
                        {new Date(event.date).toLocaleString('fr-FR', {
                          dateStyle: 'full',
                          timeStyle: 'short',
                        })}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOnIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Lieu
                      </Typography>
                      <Typography variant="body2">{event.eglise_name}</Typography>
                      {event.eglise_address && (
                        <Typography variant="caption" color="text.secondary">
                          {event.eglise_address}
                        </Typography>
                      )}
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Créé par
                      </Typography>
                      <Typography variant="body2">{event.created_by_name}</Typography>
                      {event.created_by_email && (
                        <Typography variant="caption" color="text.secondary">
                          {event.created_by_email}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Statistiques
                </Typography>
                <Stack spacing={2}>
                  {event.alarm_count !== undefined && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <NotificationsActiveIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Alarmes configurées
                        </Typography>
                        <Typography variant="body2">{event.alarm_count}</Typography>
                      </Box>
                    </Stack>
                  )}

                  {event.participant_count !== undefined && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PeopleIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Participants
                        </Typography>
                        <Typography variant="body2">{event.participant_count}</Typography>
                      </Box>
                    </Stack>
                  )}

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Créé le
                    </Typography>
                    <Typography variant="body2">
                      {new Date(event.created_at).toLocaleString()}
                    </Typography>
                  </Box>

                  {event.updated_at && event.updated_at !== event.created_at && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Dernière modification
                      </Typography>
                      <Typography variant="body2">
                        {new Date(event.updated_at).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>
      </Paper>

      {event && (
        <CreateAlarmDialog
          open={openAlarmDialog}
          onClose={() => setOpenAlarmDialog(false)}
          eventId={event.id}
          onSuccess={handleCreateAlarmSuccess}
        />
      )}
    </Box>
  );
};

export default EventDetails;
